import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { ProductForm, ProductFormData } from '@/components/products';
import { useProducerProducts } from '@/hooks/useProducerProducts';
import { useToast } from '@/hooks/use-toast';
import { useDraftSessionId, clearDraftSessionId } from '@/hooks/useDraftSessionId';
import { finalizeDraftSession } from '@/lib/media/mediaApi';

/**
 * AdminProductCreate – create-on-save pattern (no auto-draft).
 * Uses draftSessionId for media uploads before product exists.
 */
export default function AdminProductCreate() {
  const { producerId } = useParams<{ producerId: string }>();
  const navigate = useNavigate();
  const { createProduct } = useProducerProducts(producerId);
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const draftKey = `admin-product-create-${producerId ?? 'new'}`;
  const draftSessionId = useDraftSessionId(draftKey);

  const handleSave = async (data: ProductFormData, _publish: boolean) => {
    if (!data.title?.trim() || !producerId) {
      toast({ title: 'Titel krävs', variant: 'destructive' });
      return;
    }

    setIsSaving(true);
    try {
      const priceOre = Math.round(parseFloat(data.priceKr || '0') * 100);

      const product = await createProduct({
        title: data.title.trim(),
        description: data.description || undefined,
        price_ore: priceOre,
        status: data.status,
        seo_title: data.seoTitle || null,
        seo_description: data.seoDescription || null,
        seo_image_url: data.seoImageUrl || null,
        pickup_instructions: data.pickupInstructions || null,
        stock_limit: data.stockLimit,
      });

      if (product?.id) {
        try {
          await finalizeDraftSession(draftSessionId, 'product', product.id);
        } catch (err) {
          console.warn('[admin-product-create] finalizeDraftSession failed:', err);
        }
      }

      clearDraftSessionId(draftKey);
      toast({ title: 'Produkt skapad' });
      navigate(`/admin/producers/${producerId}/products`);
    } catch (err: any) {
      toast({
        title: err?.isLimitError ? 'Produktgräns nådd' : 'Kunde inte spara',
        description: err?.message || 'Försök igen.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/producers/${producerId}/products`);
  };

  return (
    <AdminLayout>
      <div className="w-full max-w-2xl mx-auto space-y-6">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2">
            <Link to={`/admin/producers/${producerId}/products`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till produkter
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold text-foreground">Skapa produkt</h1>
        </div>

        {producerId && (
          <ProductForm
            mode="create"
            producerId={producerId}
            draftSessionId={draftSessionId}
            shopifyConfigured={false}
            syncing={null}
            isSaving={isSaving}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}
      </div>
    </AdminLayout>
  );
}
